import type { GeoJsonObject, LineString, MultiLineString } from 'geojson';
import L from 'leaflet';

import { SvgIcon } from '../Extensions/SvgIcon';
import { type ExtentLayerOptions } from './Layers.types';

export interface ArrowPolyLineOptions extends L.LayerOptions {
    arrowDirection?: 'one' | 'both' | 'reverse';
}

interface ArrowPolyLine {
    options: ExtentLayerOptions;
}

class ArrowPolyLine extends L.FeatureGroup {
    readonly #geometry: LineString | MultiLineString;
    readonly #lineStyles: L.PolylineOptions[];
    constructor(geoJson: GeoJsonObject, options?: ExtentLayerOptions) {
        super(null, options);

        this.#geometry = geoJson as LineString | MultiLineString;
        const style = typeof this.options.style === 'function' ? this.options.style(L.GeoJSON.asFeature(this.#geometry)) : this.options.style;
        this.#lineStyles = [
            //{ ...style, color: '#ccc', opacity: 0.8, weight: style.weight + 2 },
            style,
        ];
    }

    onAdd(map: L.Map): this {
        this._map = map;
        if (this.getLayers().length === 0) {
            this.#lineStyles.forEach((style) => {
                this.addLayer(L.GeoJSON.geometryToLayer(L.GeoJSON.asFeature(this.#geometry), style));
                const arrows = this.createArrowSymbols(this.#geometry, style);
                if (arrows) {
                    arrows.forEach((a) => this.addLayer(a));
                }
            });
        }
        return super.onAdd(map);
    }

    onRemove(map: L.Map): this {
        this.clearLayers();

        return super.onRemove(map);
    }

    createArrowSymbols(geometry: LineString | MultiLineString, style: L.PolylineOptions): L.Marker[] {
        const multiLine = geometry.type === 'MultiLineString';
        const latLngs = L.GeoJSON.coordsToLatLngs(geometry.coordinates, multiLine ? 1 : 0) as L.LatLng[] | L.LatLng[][];

        const getArrowSizeMultiplier = (latLngs: L.LatLng[]) => {
            return Math.min(Math.max(getPixelLength(latLngs) / 5, 6), 8);
        };

        const getPixelLength = (latLngs: L.LatLng[]): number => {
            return L.GeometryUtil.length(latLngs.map((latLng) => this._map.latLngToLayerPoint(latLng)));
        };

        const lines = (multiLine ? latLngs : [latLngs]) as L.LatLng[][];

        let symbols = [];
        if (this.options.arrowDirection === 'one') {
            symbols = symbols.concat(lines.map((l) => this.createStartMarker(l, getArrowSizeMultiplier(l) + style.weight, style)));
            symbols = symbols.concat(lines.map((l) => this.createEndArrow(l, getArrowSizeMultiplier(l) + style.weight, style)));
        } else if (this.options.arrowDirection === 'reverse') {
            symbols = symbols.concat(lines.map((l) => this.createStartMarker(l.slice().reverse(), getArrowSizeMultiplier(l) + style.weight, style)));
            symbols = symbols.concat(lines.map((l) => this.createEndArrow(l.slice().reverse(), getArrowSizeMultiplier(l) + style.weight, style)));
        } else {
            // Both directions, start markers at both ends
            symbols = symbols.concat(lines.map((l) => this.createStartMarker(l, getArrowSizeMultiplier(l) + style.weight, style)));
            symbols = symbols.concat(lines.map((l) => this.createStartMarker(l.slice().reverse(), getArrowSizeMultiplier(l) + style.weight, style)));
        }

        return symbols;
    }

    createEndArrow(lineSegment: L.LatLng[], size: number, options: L.PolylineOptions): L.Marker {
        const simpleSegment = this.findFirstValidSegment(lineSegment.slice().reverse()).slice().reverse();
        const derivedBearing = L.GeometryUtil.angle(this._map, simpleSegment[0], simpleSegment[1]);

        const triangleHeight = size * Math.sqrt(3) * 0.5;
        return L.marker(simpleSegment[1], {
            icon: new SvgIcon({
                iconId: 'triangle2',
                iconSize: [size, triangleHeight],
                iconAnchor: [size / 2, triangleHeight],
                color: options.color,
                opacity: options.opacity,
            }),
            pane: this.options.arrowPane ?? 'markerPane',
            rotationAngle: derivedBearing,
            interactive: false,
            zIndexOffset: -1000000,
        });
    }

    createStartMarker(lineSegment: L.LatLng[], size: number, options: L.PolylineOptions): L.Marker {
        const simpleSegment = this.findFirstValidSegment(lineSegment);
        const derivedBearing = L.GeometryUtil.angle(this._map, simpleSegment[0], simpleSegment[1]);

        return L.marker(simpleSegment[0], {
            icon: new SvgIcon({
                iconId: 'line',
                iconSize: [size, size / 4],
                iconAnchor: [size / 2, 0],
                color: options.color,
                opacity: options.opacity,
            }),
            pane: this.options.arrowPane ?? 'markerPane',
            rotationAngle: derivedBearing,
            interactive: false,
            zIndexOffset: -1000000,
        });
    }

    // Sometimes the last segment is very short (and pointing in the wrong direction), move back to find a trustable segment
    findFirstValidSegment(line: L.LatLng[]): L.LatLng[] {
        const firstCoordinate = line[0];
        let compareCoordinateIndex = 1;
        while (compareCoordinateIndex < line.length - 2 && this.pixelDistance(firstCoordinate, line[compareCoordinateIndex]) < 1) {
            compareCoordinateIndex++;
        }
        return [firstCoordinate, line[compareCoordinateIndex]];
    }

    pixelDistance(first: L.LatLng, second: L.LatLng): number {
        const firstPoint = this._map.latLngToLayerPoint(first);
        const secondPoint = this._map.latLngToLayerPoint(second);

        return firstPoint.distanceTo(secondPoint);
    }
}

export { ArrowPolyLine };
